import type { ComponentProps } from "react";
import React, { useMemo } from "react";
import { Footer as BaseFooter, Logo } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import { Separator, useMedia, View, YStack } from "tamagui";

import { ContactUsDialog } from "~/component/feature/contact-us";

const LINKS = [
  {
    key: "aboutUs",
    href: "https://dobrobut.healthymind.help/", //https://healthymind.help",
  },
  {
    key: "terms",
    href: "https://healthymind.help/terms",
  },
  {
    key: "privacy",
    href: "https://healthymind.help/privacy",
  },

  { key: "contactUs", href: undefined },
] as const;

type FooterLink = (typeof LINKS)[number];

const FooterLinkComponent: React.FC<{ link: FooterLink }> = ({ link }) => {
  const { t } = useTranslation("footer");

  if (link.href === undefined) {
    return (
      <ContactUsDialog
        key={link.key}
        trigger={
          <BaseFooter.Link font="md">{t(`links.${link.key}`)}</BaseFooter.Link>
        }
      />
    );
  }

  return (
    <BaseFooter.Link font="md" key={link.key} href={link.href}>
      {t(`links.${link.key}`)}
    </BaseFooter.Link>
  );
};

function renderFooterLink(link: FooterLink) {
  return <FooterLinkComponent key={link.key} link={link} />;
}

type FooterProps = ComponentProps<typeof BaseFooter.Container>;

const FooterMobile: React.FC<FooterProps> = ({ ...props }) => {
  const { t } = useTranslation("footer");
  const year = useMemo(() => new Date().getFullYear(), []);

  return (
    <BaseFooter.Container paddingBottom="$3" {...props}>
      <BaseFooter.Content media="md" gap={"$2"}>
        <Logo style={{ height: 28, width: 166 }} />
        <BaseFooter.Links media="md">
          {LINKS.map(renderFooterLink)}
        </BaseFooter.Links>
      </BaseFooter.Content>
      <Separator width="75%" alignSelf="center" borderColor="$gray-600" />
      <BaseFooter.Content media="md">
        <BaseFooter.Disclaimer>{t("disclaimer")}</BaseFooter.Disclaimer>
      </BaseFooter.Content>
      <Separator width="75%" alignSelf="center" borderColor="$gray-600" />
      <BaseFooter.Content media="md">
        <BaseFooter.CopyrightText>
          {t("copyright", { year })}
        </BaseFooter.CopyrightText>
      </BaseFooter.Content>
    </BaseFooter.Container>
  );
};

export const FooterDefault: React.FC<FooterProps> = ({ ...props }) => {
  const { t } = useTranslation("footer");
  const year = useMemo(() => new Date().getFullYear(), []);

  return (
    <BaseFooter.Container paddingTop={"$1.2"} {...props}>
      <BaseFooter.Content>
        <BaseFooter.Links media="lg">
          <BaseFooter.LinksTitle>{t("title")}</BaseFooter.LinksTitle>
          {LINKS.map(renderFooterLink)}
        </BaseFooter.Links>
        <View flexDirection="column" gap={"$3"} maxWidth={260}>
          <BaseFooter.Disclaimer>{t("disclaimer")}</BaseFooter.Disclaimer>
          <BaseFooter.CopyrightText>
            {t("copyright", { year })}
          </BaseFooter.CopyrightText>
        </View>
      </BaseFooter.Content>

      <BaseFooter.Content>
        <BaseFooter.LogoContainer media="lg">
          <BaseFooter.Logo />
        </BaseFooter.LogoContainer>
      </BaseFooter.Content>
    </BaseFooter.Container>
  );
};

export const Footer: React.FC<FooterProps> = (props) => {
  const { sm } = useMedia();

  if (sm) return <FooterMobile {...props} />;
  else return <FooterDefault {...props} />;
};

const LINKS_AUTH = LINKS.filter(
  (l) => l.key === "terms" || l.key === "privacy",
);

export const FooterAuth: React.FC<FooterProps> = (props) => {
  const { t } = useTranslation("footer");
  const { sm } = useMedia();
  return (
    <BaseFooter.Container
      gap={"$3"}
      paddingTop={"$1"}
      paddingBottom={"$3"}
      topLineProps={{ borderColor: sm ? "$gray-primary-900" : "$gray-200" }}
      {...props}
    >
      <BaseFooter.Content media={sm ? "md" : "lg"}>
        <YStack gap={"$1"}>
          <BaseFooter.Logo />
          <BaseFooter.Links gap={"$1"} $sm={{ alignItems: "center" }}>
            {LINKS_AUTH.map(renderFooterLink)}
          </BaseFooter.Links>
        </YStack>
        <YStack gap={"$2"} maxWidth={sm ? "100%" : 420} alignItems="center">
          {sm && <BaseFooter.Divider marginTop={"$2"} />}
          <BaseFooter.Disclaimer>{t("disclaimer")}</BaseFooter.Disclaimer>
        </YStack>
      </BaseFooter.Content>
    </BaseFooter.Container>
  );
};
